// lib
import React, { useContext, useState, useRef, useMemo, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import ADND from '@aytm/lib/adnd/ADND'
import { DataContext } from '@aytm/app/stats/page/sidebar/filters/FiltersPanel'

// components
import { RadioButton } from 'primereact/radiobutton'
import { Dropdown } from 'primereact/dropdown'
import { MultiSelect } from 'primereact/multiselect'
import { MultiStateMultiSelect } from 'primereact/multistatemultiselect'

import DropdownSlider from 'primereact-raytm-theme/DropdownSlider'
import MultiSelectHeader from 'primereact-raytm-theme/MultiSelectHeader'
import MultiSelectFooter from 'primereact-raytm-theme/MultiSelectFooter'

import { optionGroupTemplate } from '@aytm/app/stats/page/components/lib/templates'

// styles
import style from '@aytm/app/stats/page/sidebar/filters/styles/FiltersPanel.module.scss'

const FiltersRow = ({
  mode,
  group,
  filter,
  filterValues,
  filterOptions,
  error,
  processing,
  onRemoveVariable,
  onDNDStart,
  onDNDEnd
}) => {
  const {
    windowUID,
    disabled,
    dropdownsRef,
    restoreQuestion,
    onScrollToQuestion,
    onChangeBasicFilter,
    onResetBasicFilter,
    onShowFilterDropdown,
    onHideFilterDropdown
  } = useContext(DataContext)

  const [values, setValues] = useState(null !== filterValues ? JSON.parse(filterValues) : filterValues)
  const [options, setOptions] = useState(filterOptions)

  const [restoreDisabled, setRestoreDisabled] = useState(false)

  const filterRef = useRef(null)

  const dndListItemRef = useRef()

  const filterDisabled = useMemo(
    () => disabled || filter.disabled || (group.external && 'function' != typeof group.onChange),
    [disabled, filter.disabled, group.external, group.onChange]
  )
  const filterSelected = useMemo(
    () =>
      'advanced' == mode || 'radiobutton' == filter.type || filterDisabled
        ? false
        : 'basic' == mode || group.external
        ? 'sliderselect' == filter.type
          ? `1-${options.length}` != values.join('-')
          : null !== values && (0 < values.length || 'button' == filter.type)
        : false,
    [mode, filter.type, group.external, values, options, filterDisabled]
  )

  const filterFlatOptions = useMemo(
    () =>
      ['multistatemultiselect', 'multiselect'].includes(filter.type) && filter.group
        ? options.flatMap(option => option.items)
        : options,
    [filter.type, filter.group, options]
  )

  const onChangeSlider = useCallback(
    event => {
      const values = [Math.max(event.value[0], 1), Math.min(event.value[1], options.length)]
      values.sort((a, b) => a - b)

      setValues(values)
    },
    [options]
  )

  const onChangeFilter = useCallback(
    event => {
      if (group.external) group.onChange(event)
      else {
        switch (filter.type) {
          case 'sliderselect': {
            const values = [Math.max(event.value[0], 1), Math.min(event.value[1], options.length)]
            values.sort((a, b) => a - b)

            onChangeBasicFilter(filter, values)
            break
          }
          case 'dropdown': {
            const values = [event.value]

            onChangeBasicFilter(filter, values)
            break
          }
          case 'multistatemultiselect': {
            const values = event.value
            values.sort(
              (a, b) =>
                filterFlatOptions.findIndex(v => v.value == a.value) -
                filterFlatOptions.findIndex(v => v.value == b.value)
            )

            onChangeBasicFilter(filter, values)
            break
          }
          case 'multiselect': {
            const values = event.value
            values.sort(
              (a, b) => filterFlatOptions.findIndex(v => v.value == a) - filterFlatOptions.findIndex(v => v.value == b)
            )

            onChangeBasicFilter(filter, values)
            break
          }
          case 'button': {
            onChangeBasicFilter(filter, null)
            break
          }
          case 'radiobutton': {
            onChangeBasicFilter(filter, event.value)
            break
          }
          default: {
            break
          }
        }
      }
    },
    [filter, group, options, filterFlatOptions, onChangeBasicFilter]
  )

  const fetchData = {
    key: (filter.id || filter.key).split('-')[1],
    model: null,
    windowUID
  }
  const dndListItem =
    !disabled && filter.hasHandle
      ? ADND.sources
          .data({
            type: `${group.type}-${windowUID}`,
            clearDragImage: false
          })
          .onStart(onDNDStart)
          .onEnd(onDNDEnd)
          .fetchData(() => fetchData)
      : null

  const onRestoreQuestion = () => {
    setRestoreDisabled(true)
    restoreQuestion(filter.id)
  }

  const onShowDropdown = () => {
    if (null === filterRef.current) return
    onShowFilterDropdown(filterRef.current)
  }

  const onHideDropdown = () => {
    if (null === filterRef.current) return
    onHideFilterDropdown(filterRef.current)
  }

  const onRemoveClick = () => {
    if (Object.hasOwn(filter, 'derived')) {
      for (const v of filter.derived) {
        onRemoveVariable(v)
      }
    } else onRemoveVariable(filter.key)
  }

  const itemTemplate = option =>
    option.disabled && option.icon == undefined ? (
      <span dangerouslySetInnerHTML={{ __html: option.label }}></span>
    ) : (
      <span className="p-item-label">
        {option.icon ? <i className={`p-item-icon icon-aytmfd-${option.icon}`} /> : null}
        <span dangerouslySetInnerHTML={{ __html: option.label }}></span>
      </span>
    )

  const labelTemplate = useCallback(
    value =>
      'sliderselect' == filter.type ? (
        <span
          dangerouslySetInnerHTML={{
            __html: options[Math.min(Math.max(value, 1), options.length) - 1].label
          }}
        ></span>
      ) : null,
    [filter.type, options]
  )

  const buttonLabelTemplate = useCallback(
    event => {
      let label = filter.title

      if ('sliderselect' == filter.type) {
        label =
          event.sliderProps != undefined && event.sliderProps.value != undefined && 2 <= event.sliderProps.value.length
            ? `${event.sliderProps.min}-${event.sliderProps.max}` == event.sliderProps.value.join('-')
              ? filter.title
              : `${filter.title}: ${options[event.sliderProps.value[0] - 1].label} &ndash; ${
                  options[event.sliderProps.value[1] - 1].label
                }`
            : filter.title
      } else if ('dropdown' == filter.type) {
        if (values != undefined && 0 < values.length) label = `${filter.title}: ${values[0]}`
      } else if (event != undefined && event.value != undefined) {
        if (
          (('multistatemultiselect' == filter.type && !event.value.some(item => !item.state)) ||
            'multiselect' == filter.type) &&
          filterFlatOptions.length == event.value.length
        ) {
          label = `${event.placeholder}: All`
        } else if (0 < event.value.length) {
          label =
            1 == event.value.length && ('multiselect' == filter.type || event.value[0].state)
              ? `${event.placeholder}: ${
                  filterFlatOptions.find(
                    o => o.value == ('multiselect' == filter.type ? event.value[0] : event.value[0][event.optionValue])
                  )[event.optionLabel]
                }`
              : `${event.placeholder}: Filtered to ${event.value.length}`
        }
      } else if (event != undefined) label = event.placeholder

      return (
        <>
          {filter.icon ? <i className={`icon-aytmfd-${filter.icon}`} /> : null}
          <span dangerouslySetInnerHTML={{ __html: label }}></span>
        </>
      )
    },
    [filter, values, options, filterFlatOptions]
  )

  useEffect(() => {
    if (dndListItem) dndListItem.connect(dndListItemRef.current)

    return () => {
      if (dndListItem) dndListItem.disconnect()
    }
  }, [dndListItem])

  useEffect(() => {
    const _values = null !== filterValues ? JSON.parse(filterValues) : filterValues
    setValues(_values)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValues])
  useEffect(() => {
    setOptions(filterOptions)
  }, [filterOptions])

  useEffect(() => {
    if ('questions' == group.key && ['dropdown', 'multistatemultiselect', 'multiselect'].includes(filter.type)) {
      dropdownsRef.current[filter.key] = filterRef
    }

    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      delete dropdownsRef.current[filter.key]
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, group, filterRef, dropdownsRef.current]) // TODO

  const renderComponent = () => {
    switch (filter.type) {
      case 'sliderselect': {
        return (
          <DropdownSlider
            dropdownClassName={
              null !== values && `1-${options.length}` != values.join('-') ? 'p-subtheme-contrast' : ''
            }
            range
            min={1}
            max={options.length}
            value={values}
            labelTemplate={labelTemplate}
            buttonLabelTemplate={buttonLabelTemplate}
            disabled={filterDisabled || !options.length}
            onSlideEnd={filterDisabled || !options.length ? null : onChangeFilter}
            onChange={filterDisabled || !options.length ? null : onChangeSlider}
          />
        )
      }
      case 'dropdown': {
        return (
          <Dropdown
            ref={filterRef}
            className={null !== values && 0 < values.length ? 'p-subtheme-contrast' : ''}
            placeholder={filter.title}
            value={values}
            options={options}
            optionValue="value"
            optionLabel="label"
            itemTemplate={itemTemplate}
            valueTemplate={buttonLabelTemplate}
            filter={filter && Array.isArray(options) && options.some(o => !o.disabled)}
            filterBy="search,label"
            filterPlaceholder="Search items"
            resetFilterOnHide={true}
            filterInputAutoFocus={false}
            fixedDropdownDir="bottom"
            disabled={filterDisabled || !options.length}
            onChange={filterDisabled || !options.length ? null : onChangeFilter}
            onShow={filterDisabled || !options.length ? null : onShowDropdown}
            onHide={filterDisabled || !options.length ? null : onHideDropdown}
          />
        )
      }
      case 'multistatemultiselect': {
        return (
          <MultiStateMultiSelect
            ref={filterRef}
            className={null !== values && 0 < values.length ? 'p-subtheme-contrast' : ''}
            placeholder={filter.title}
            value={values}
            options={options}
            optionValue="value"
            optionLabel="label"
            optionGroupLabel={filter.group ? 'text' : null}
            optionGroupChildren={filter.group ? 'items' : null}
            itemTemplate={itemTemplate}
            optionGroupTemplate={filter.group ? optionGroupTemplate : null}
            buttonLabelTemplate={buttonLabelTemplate}
            panelHeaderTemplate={MultiSelectHeader}
            panelFooterTemplate={MultiSelectFooter}
            filter={filterFlatOptions.some(o => !o.disabled)}
            filterBy="search,label"
            filterPlaceholder="Search items"
            resetFilterOnHide={true}
            // filterInputAutoFocus={false}
            fixedDropdownDir="bottom"
            disabled={filterDisabled || !options.length}
            onChange={filterDisabled || !options.length ? null : onChangeFilter}
            onShow={filterDisabled || !options.length ? null : onShowDropdown}
            onHide={filterDisabled || !options.length ? null : onHideDropdown}
          />
        )
      }
      case 'multiselect': {
        return (
          <MultiSelect
            ref={filterRef}
            className={null !== values && 0 < values.length ? 'p-subtheme-contrast' : ''}
            placeholder={filter.title}
            value={values}
            options={options}
            optionValue="value"
            optionLabel="label"
            optionGroupLabel={filter.group ? 'text' : null}
            optionGroupChildren={filter.group ? 'items' : null}
            itemTemplate={itemTemplate}
            optionGroupTemplate={filter.group ? optionGroupTemplate : null}
            buttonLabelTemplate={buttonLabelTemplate}
            panelHeaderTemplate={MultiSelectHeader}
            panelFooterTemplate={MultiSelectFooter}
            filter={filterFlatOptions.some(o => !o.disabled)}
            filterBy="search,label"
            filterPlaceholder="Search items"
            resetFilterOnHide={true}
            // filterInputAutoFocus={false}
            fixedDropdownDir="bottom"
            disabled={filterDisabled || !options.length}
            onChange={filterDisabled || !options.length ? null : onChangeFilter}
            onShow={filterDisabled || !options.length ? null : onShowDropdown}
            onHide={filterDisabled || !options.length ? null : onHideDropdown}
          />
        )
      }
      case 'button': {
        return (
          <div
            className={`${style['filter-button']}${
              null !== values ? ' ' + style['filter-button-active'] + ' p-subtheme-contrast' : ''
            }${filterDisabled ? ' ' + style['filter-button-disabled'] + ' p-disabled' : ''}`}
            onClick={filterDisabled ? null : onChangeFilter}
          >
            {filter.icon ? <i className={`icon-aytmfd-${filter.icon}`} /> : null}
            <span dangerouslySetInnerHTML={{ __html: filter.title }}></span>
          </div>
        )
      }
      case 'radiobutton': {
        return options.map(option => (
          <div
            key={`${filter.key}-${option.value}`}
            className={`${style['filter-radio']}${filterDisabled ? '' : ' p-subtheme-contrast'}`}
          >
            <RadioButton
              value={option.value}
              checked={values == option.value}
              disabled={filterDisabled}
              onChange={filterDisabled ? null : onChangeFilter}
            />
            <span
              className={classNames(style['row-label'], { 'p-disabled': filterDisabled })}
              onClick={filterDisabled ? null : onChangeFilter.bind(this, option)}
              dangerouslySetInnerHTML={{ __html: option.label }}
            ></span>
          </div>
        ))
      }
      default: {
        return null
      }
    }
  }

  return (
    <div className={`${style['filter-row']} ${style[`offset-${filter.lvl || 0}`]} p-fluid`} data-id={filter.key}>
      {filterSelected && (
        <span
          className={`${style['filter-reset']}${filterDisabled ? ' p-disabled' : ''}`}
          onClick={filterDisabled ? null : group.external ? group.onChange : () => onResetBasicFilter(filter)}
        >
          <i className="icon-aytmfd-delete" />
        </span>
      )}
      <div ref={dndListItemRef} className="PRAYTMTheme">
        {'basic' == mode || group.external ? (
          renderComponent()
        ) : (
          <div className={`${style['filter-button']} p-disabled`}>
            {filter.icon ? <i className={`icon-aytmfd-${filter.icon}`} /> : null}
            <span dangerouslySetInnerHTML={{ __html: filter.title }}></span>
          </div>
        )}
        {group.remove && !group.remove.disabled && !filterDisabled && (
          <span className={style['filter-remove']} onClick={group.external ? group.onChange : onRemoveClick}>
            <i className={processing ? 'icon-aytmfd-refresh-v2 aytmfd-spin' : 'icon-aytmfd-trash'} />
          </span>
        )}
        {!disabled && filter.hasHandle && (
          <span className={style['filter-handle']}>
            <i className="icon-aytmfd-handle" />
          </span>
        )}
      </div>
      {!disabled && filter.hasHandle && (
        <span className={style['filter-restore']} onClick={restoreDisabled ? null : onRestoreQuestion} />
      )}
      {filter.hasScrollTo && (
        <span className={style['filter-scroll']} data-id={filter.id} onClick={() => onScrollToQuestion(filter.id)} />
      )}
      {error && <div className={style['filter-error-message']}>There was some problem during deletion</div>}
    </div>
  )
}

FiltersRow.propTypes = {
  mode: PropTypes.string,
  group: PropTypes.object,
  filter: PropTypes.object,
  filterValues: PropTypes.any,
  filterOptions: PropTypes.any,
  error: PropTypes.bool,
  processing: PropTypes.bool,
  onRemoveVariable: PropTypes.func,
  onDNDStart: PropTypes.func,
  onDNDEnd: PropTypes.func
}

FiltersRow.displayName = 'FiltersRow'

export default FiltersRow

ADND().connect()
